import { TmElement, loadInlineStyles } from '@thiememeulenhoff/lit-sdk';
import { IconName } from '@thiememeulenhoff/types';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { translate } from '@thiememeulenhoff/translations';
import { tagName } from '../../helpers';
import '../slider/slider.component';
import { PlayerError, PlayerState } from './read-speaker-state.enum';
import { ReadSpeakerEvent, ReadSpeakerService } from './read-speaker.service';
import { styles } from './read-speaker.styles';

const iconMap: Record<PlayerState, IconName> = {
  init: 'voiceover',
  loading: 'loading',
  playing: 'pause',
  paused: 'play',
  stopped: 'voiceover',
  speed: 'gauge',
};

const tag = tagName('read-speaker');

@customElement(tag)
export class ReadSpeakerComponent extends TmElement {
  readonly #readSpeakerService = ReadSpeakerService.get();

  @property({ type: String, reflect: true })
  elementId = Math.random().toString(36).slice(2, 7);

  @property({ type: String, attribute: 'read-class', reflect: true })
  readClass = 'readthis';

  @property({ attribute: 'scroll-container' })
  scrollContainer = '';

  @property({ type: String, reflect: true })
  language = 'nl_nl';

  @property()
  shadowDomSelectors = [
    'tmwc-content-player',
    'tmwc-content-theory',
    'tmwc-content-view',
  ];

  @state()
  sliderValue = 50;

  @state()
  showSlider = false;

  @state()
  state: PlayerState = PlayerState.Init;

  protected override createRenderRoot() {
    return this; // https://lit.dev/docs/components/shadow-dom/#implementing-createrenderroot
  }

  override render() {
    loadInlineStyles(this, styles);
    const classes = {
      init: this.state === PlayerState.Init,
      loading: this.state === PlayerState.Loading,
      playing: this.state === PlayerState.Playing,
      paused: this.state === PlayerState.Paused,
      stopped: this.state === PlayerState.Stopped,
      speed: this.state === PlayerState.Speed,
    };

    const togglesWhenPlayer =
      this.state === PlayerState.Playing || this.state === PlayerState.Paused
        ? html`<button
              @click=${this.#speedToggle}
              class="button settings-button"
              type="button"
            >
              <a-icon class="settings-icon" name="gauge"></a-icon>
            </button>
            <div ?hidden="${!this.showSlider}">
              <a-slider
                value="${this.sliderValue}"
                @change="${this.#speedChange}"
                label=""
                step="5"
                min="5"
                max="200"
              ></a-slider>
            </div>`
        : nothing;

    const readSpeakerButtonTemplate = html`<div
      id="${this.#id}"
      class="rs_skip rsbtn rs_preserve"
    >
      <a
        rel="nofollow"
        id="${this.#id}-play"
        class="rsbtn_play"
        accesskey="L"
        title="Laat de tekst voorlezen met ReadSpeaker"
        href="https://app-eu.readspeaker.com/cgi-bin/rsent?customerid=5797&amp;lang=${this
          .language}&amp;readclass=${this.readClass}"
      >
        <span class="rsbtn_left rsimg rspart">
          <span class="rsbtn_text"><span>Lees voor</span></span>
        </span>
        <span class="rsbtn_right rsimg rsplay rspart"></span>
      </a>
    </div>`;

    return html`
      <div class="rs_skip read-speaker ${classMap(classes)}">
        <button
          @click=${this.#toggle}
          class="button main-button"
          type="button"
          aria-label="${translate('button.readspeaker.aria_label')}"
        >
          <a-icon class="main-icon" name=${iconMap[this.state]}></a-icon>
        </button>
        ${togglesWhenPlayer}
        <button @click=${this.#stop} class="button close-button" type="button">
          <a-icon class="close-icon" name="close"></a-icon>
        </button>
      </div>
      ${readSpeakerButtonTemplate}
    `;
  }

  get #id() {
    return `read-speaker-button-${this.elementId}`;
  }

  #toggle() {
    if (this.state === PlayerState.Playing) {
      this.#pause();
      return;
    }

    this.#play();
  }

  #play() {
    this.#readSpeakerService.register(
      this.querySelector(`#${this.#id}`) as HTMLElement,
      this.#handleReadSpeakerEvent.bind(this),
    );

    if (this.state === PlayerState.Paused) {
      this.#readSpeakerService.resume();
    } else {
      this.state = PlayerState.Loading;
      this.#readSpeakerService.play(this.#id);
    }

    this.sliderValue = this.#readSpeakerService.speed;
  }

  #pause() {
    this.#readSpeakerService.pause();
  }

  #stop() {
    this.#readSpeakerService.stop();
  }

  #speedToggle() {
    this.showSlider = !this.showSlider;
  }

  #speedChange(value: CustomEvent) {
    this.sliderValue = Number(value.detail);
    this.#readSpeakerService.speed = this.sliderValue;
  }

  #handleReadSpeakerEvent(event: ReadSpeakerEvent) {
    switch (event.event) {
      case PlayerState.Playing:
      case PlayerState.Paused:
      case PlayerState.Stopped:
        this.state = event.event;
        this.showSlider = false;
        this.dispatchLocalEvent({
          element: this,
          eventName: event.event,
        });
        break;
      case PlayerError.AudioLoadError:
      case PlayerError.AudioNoSound:
        this.dispatchLocalEvent({
          element: this,
          eventName: PlayerState.Stopped,
        });
        throw Error(`ReadSpeaker audio error: ${event.event}`);
      default:
        break;
    }
  }

  #findScrollParent(element: Element): Element | null {
    if (element.matches(this.scrollContainer)) {
      return element;
    }

    const shadowElement = element.shadowRoot?.querySelector(
      this.scrollContainer,
    );
    if (shadowElement) {
      return shadowElement;
    }

    if (element.parentElement) {
      return this.#findScrollParent(element.parentElement);
    }

    return null;
  }

  #contentContainer(contentElement: Element) {
    // Importing the TheoryComponent is not possible because of circular dependencies, so we try to select
    // the element that contains the theory. If this succeeds the contentElement is a TheoryComponent
    const innerElement = contentElement.shadowRoot
      ?.querySelector('#view')
      ?.shadowRoot?.querySelector('div.readthis');

    return innerElement ?? contentElement;
  }

  override onCreate = () => {
    if (this.scrollContainer !== '') {
      window.setTimeout(() => this.#registerScrollContainer());
    }
  };

  #registerScrollContainer() {
    const contentElement = document.body.querySelector(`.${this.readClass}`);

    if (!(contentElement instanceof Element)) {
      return;
    }

    const scrollContainer = this.#findScrollParent(contentElement);
    const contentContainer = this.#contentContainer(contentElement);
    if (
      scrollContainer instanceof Element &&
      contentContainer instanceof Element
    ) {
      this.#readSpeakerService.registerScrollContainer(
        this.elementId,
        scrollContainer,
        contentContainer,
      );
    }
  }

  override onDestroy = () => {
    this.#readSpeakerService.unregisterScrollContainer(this.elementId);
  };
}
